import React, { useState, useEffect, useCallback } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Check,
  AlertCircle,
  Loader2,
  Info,
  RotateCcw,
} from "lucide-react";

// TypeScript interfaces for step data
export interface StepDataBase {
  [key: string]: any;
}

export interface StepperStorageFormat {
  currentStep: number;
  completedSteps: number[];
  skippedSteps: number[];
  stepData: Record<number, StepDataBase>;
  timestamp?: number;
  version?: string;
}

export interface StepHelpers {
  // Storage operations
  getStepData: <T extends StepDataBase = StepDataBase>(
    stepIndex: number
  ) => T | undefined;
  setStepData: <T extends StepDataBase = StepDataBase>(
    stepIndex: number,
    data: Partial<T>
  ) => void;
  clearStepData: (stepIndex: number) => void;
  getAllStepData: () => Record<number, StepDataBase>;

  // Cross-step operations
  getDataFromStep: <T = any>(
    stepIndex: number,
    fieldPath: string
  ) => T | undefined;
  setDataToStep: <T = any>(
    stepIndex: number,
    fieldPath: string,
    value: T
  ) => void;
  copyDataBetweenSteps: (
    fromStep: number,
    toStep: number,
    fieldMapping?: Record<string, string>
  ) => void;

  // Validation and state
  hasStepData: (stepIndex: number) => boolean;
  isStepDataEmpty: (stepIndex: number) => boolean;
  getStepCompletionStatus: (
    stepIndex: number
  ) => "completed" | "skipped" | "active" | "available" | "disabled";

  // Data aggregation
  collectDataFromSteps: <T = any>(
    stepIndexes: number[],
    fieldPath: string
  ) => T[];
  aggregateStepData: <
    T extends Record<string, any> = Record<string, any>
  >() => T;

  // Utility
  exportStepperData: () => StepperStorageFormat;
  importStepperData: (data: StepperStorageFormat) => void;
  resetAllData: () => void;
}

/**
 * A flexible stepper component for multi-step forms and wizards.
 * Navigation is handled at the step component level.
 *
 * For detailed documentation and usage examples, please visit:
 * https://docs.google.com/document/d/18EyxVP4WAWYnp2pQrCQ9-CoVN4LfmXKkFW5Ck5Lqsyo/edit?usp=sharing
 */
export const StepperV2 = ({
  steps = [],
  onStepChange,
  onComplete,
  onValidation,
  autoSave = true,
  persistProgress = true,
  storageKey = "stepper-progress",
  allowSkip = false,
  enableRouting = true,
  basePath = "",
  className = "",
  onStepSave, // Optional callback for saving step data
  enableApiSave = false, // Enable API saving functionality
  saveOnStepChange = false, // Auto-save when changing steps
  trackDirtyState = false, // Track if step data has changed
  // NEW: Control draft mode - when true, no localStorage operations
  showDraftResume = false,
}) => {
  const [currentStep, setCurrentStep] = useState(0); // Start with 0 as default
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [skippedSteps, setSkippedSteps] = useState(new Set());
  const [validationErrors, setValidationErrors] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const [stepData, setStepData] = useState({});
  const [draftStep, setDraftStep] = useState(0);
  const [dirtySteps, setDirtySteps] = useState(new Set());
  const [originalStepData, setOriginalStepData] = useState({});
  const [saveStatus, setSaveStatus] = useState("idle"); // 'idle', 'saving', 'saved', 'error'
  const [isDraftMode, setIsDraftMode] = useState(!showDraftResume); // Track if we're in draft mode (inverted logic)
  const [showDraftBanner, setShowDraftBanner] = useState(true); // Always show banner initially
  const [hasStoredProgress, setHasStoredProgress] = useState(false); // Track if localStorage has data
  const [isInitialized, setIsInitialized] = useState(false); // Track if component is initialized

  // Helper function to get nested property value
  const getNestedValue = (obj: any, path: string): any => {
    return path.split(".").reduce((current, key) => current?.[key], obj);
  };

  // Helper function to set nested property value
  const setNestedValue = (obj: any, path: string, value: any): void => {
    const keys = path.split(".");
    const lastKey = keys.pop();
    const target = keys.reduce((current, key) => {
      if (!(key in current)) {
        current[key] = {};
      }
      return current[key];
    }, obj);
    if (lastKey) {
      target[lastKey] = value;
    }
  };

  // Create step helpers object
  const createStepHelpers = (): StepHelpers => ({
    // Storage operations
    getStepData: <T extends StepDataBase = StepDataBase>(
      stepIndex: number
    ): T | undefined => {
      return stepData[stepIndex] as T;
    },

    setStepData: <T extends StepDataBase = StepDataBase>(
      stepIndex: number,
      data: Partial<T>
    ): void => {
      updateStepData(stepIndex, data);
    },

    clearStepData: (stepIndex: number): void => {
      setStepData((prev) => {
        const newData = { ...prev };
        delete newData[stepIndex];
        return newData;
      });
    },

    getAllStepData: (): Record<number, StepDataBase> => {
      return { ...stepData };
    },

    // Cross-step operations
    getDataFromStep: <T = any,>(
      stepIndex: number,
      fieldPath: string
    ): T | undefined => {
      const data = stepData[stepIndex];
      if (!data) return undefined;
      return getNestedValue(data, fieldPath) as T;
    },

    setDataToStep: <T = any,>(
      stepIndex: number,
      fieldPath: string,
      value: T
    ): void => {
      const currentData = stepData[stepIndex] || {};
      const newData = { ...currentData };
      setNestedValue(newData, fieldPath, value);
      updateStepData(stepIndex, newData);
    },

    copyDataBetweenSteps: (
      fromStep: number,
      toStep: number,
      fieldMapping?: Record<string, string>
    ): void => {
      const sourceData = stepData[fromStep];
      if (!sourceData) return;

      const targetData = stepData[toStep] || {};
      const newTargetData = { ...targetData };

      if (fieldMapping) {
        // Copy specific fields with mapping
        Object.entries(fieldMapping).forEach(([sourceField, targetField]) => {
          const value = getNestedValue(sourceData, sourceField);
          if (value !== undefined) {
            setNestedValue(newTargetData, targetField, value);
          }
        });
      } else {
        // Copy all fields
        Object.assign(newTargetData, sourceData);
      }

      updateStepData(toStep, newTargetData);
    },

    // Validation and state
    hasStepData: (stepIndex: number): boolean => {
      return (
        stepIndex in stepData &&
        Object.keys(stepData[stepIndex] || {}).length > 0
      );
    },

    isStepDataEmpty: (stepIndex: number): boolean => {
      const data = stepData[stepIndex];
      return !data || Object.keys(data).length === 0;
    },

    getStepCompletionStatus: (stepIndex: number) => {
      return getStepStatus(stepIndex);
    },

    // Data aggregation
    collectDataFromSteps: <T = any,>(
      stepIndexes: number[],
      fieldPath: string
    ): T[] => {
      return stepIndexes
        .map((stepIndex) => getNestedValue(stepData[stepIndex], fieldPath))
        .filter((value) => value !== undefined) as T[];
    },

    aggregateStepData: <
      T extends Record<string, any> = Record<string, any>
    >(): T => {
      const aggregated = {} as T;
      Object.values(stepData).forEach((data) => {
        Object.assign(aggregated, data);
      });
      return aggregated;
    },

    // Utility
    exportStepperData: (): StepperStorageFormat => {
      return {
        currentStep,
        completedSteps: Array.from(completedSteps) as number[],
        skippedSteps: Array.from(skippedSteps) as number[],
        stepData,
        timestamp: Date.now(),
        version: "1.0.0",
      };
    },

    importStepperData: (data: StepperStorageFormat): void => {
      // Don't import currentStep as URL controls navigation
      setCompletedSteps(new Set(data.completedSteps || []));
      setSkippedSteps(new Set(data.skippedSteps || []));
      setStepData(data.stepData || {});
    },

    resetAllData: (): void => {
      startFresh();
    },
  });

  const getStepSlug = (stepIndex) => {
    if (
      !steps ||
      steps.length === 0 ||
      stepIndex < 0 ||
      stepIndex >= steps.length
    ) {
      return "step-1";
    }
    return steps[stepIndex]?.slug || `step-${stepIndex + 1}`;
  };

  // Get step index from slug
  const getStepFromSlug = (slug) => {
    if (!steps || steps.length === 0) return 0; // Default to first step
    if (!slug) return 0; // Default to first step
    const index = steps.findIndex((step) => step.slug === slug);
    return index >= 0 ? index : 0; // Default to first step if not found
  };

  // Update URL - this should always reflect the current step
  const updateURL = useCallback(
    (stepIndex) => {
      if (enableRouting && typeof window !== "undefined") {
        const slug = getStepSlug(stepIndex);
        const newHash = `#${slug}`;

        // Only update if the hash has actually changed
        if (window.location.hash !== newHash) {
          // Use replaceState to avoid adding to browser history for programmatic navigation
          // and pushState for user-initiated navigation
          const currentUrl = window.location.href.split("#")[0];
          const newUrl = `${currentUrl}${newHash}`;

          // Replace the URL without triggering navigation
          window.history.replaceState(null, "", newUrl);
          console.log("Hash updated to:", newHash);

          // Force trigger hashchange event for consistency
          setTimeout(() => {
            const event = new HashChangeEvent("hashchange", {
              oldURL: window.location.href,
              newURL: newUrl,
            });
            window.dispatchEvent(event);
          }, 0);
        }
      }
    },
    [enableRouting, steps]
  );

  // Get step from current URL
  const getCurrentStepFromURL = useCallback(() => {
    if (!enableRouting || typeof window === "undefined") return 0;

    const hash = window.location.hash;
    const slug = hash.replace("#", "");

    if (!slug) return 0;

    const stepIndex = getStepFromSlug(slug);
    // Validate step index is within bounds
    if (stepIndex >= 0 && stepIndex < steps.length) {
      return stepIndex;
    }
    return 0;
  }, [enableRouting, steps]);

  // Handle hash changes - URL is the source of truth
  useEffect(() => {
    if (!enableRouting || typeof window === "undefined" || !isInitialized)
      return;

    const handleHashChange = () => {
      const urlStep = getCurrentStepFromURL();
      console.log(
        "Hash changed - URL step:",
        urlStep,
        "Current step:",
        currentStep
      );

      // Always follow the URL
      if (urlStep !== currentStep) {
        console.log("Updating current step from URL:", urlStep);
        setCurrentStep(urlStep);

        if (onStepChange) {
          onStepChange(urlStep, currentStep, {
            stepData: stepData[urlStep],
            isCompleted: completedSteps.has(urlStep),
            isSkipped: skippedSteps.has(urlStep),
          });
        }
      }
    };

    // Listen to both hashchange and popstate events
    window.addEventListener("hashchange", handleHashChange);
    window.addEventListener("popstate", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      window.removeEventListener("popstate", handleHashChange);
    };
  }, [
    enableRouting,
    isInitialized,
    currentStep,
    onStepChange,
    stepData,
    completedSteps,
    skippedSteps,
    getCurrentStepFromURL,
  ]);

  // Initialize component and set up URL routing
  useEffect(() => {
    if (steps.length === 0) return;

    console.log("=== INITIALIZING STEPPER ===");

    // Load saved progress first (but don't use currentStep from it)
    let savedData = null;
    if (persistProgress && storageKey) {
      try {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          savedData = JSON.parse(saved);
          setHasStoredProgress(true);

          // Only apply saved data if showDraftResume is true (normal mode)
          if (showDraftResume) {
            setCompletedSteps(new Set(savedData.completedSteps || []));
            setSkippedSteps(new Set(savedData.skippedSteps || []));
            setStepData(savedData.stepData || {});
            setDraftStep(savedData.currentStep || 0);
            console.log("Loaded saved data (excluding currentStep)");
          } else {
            // In draft mode, just set draftStep for display but don't load data
            setDraftStep(savedData.currentStep || 0);
          }
        }
      } catch (error) {
        console.error("Failed to load stepper progress:", error);
      }
    }

    // Determine initial step from URL (URL has absolute priority)
    let initialStep = 0;

    if (enableRouting && typeof window !== "undefined") {
      const urlStep = getCurrentStepFromURL();
      initialStep = urlStep;
      console.log("Using URL step:", initialStep);
    }

    // Set the initial step
    console.log("Setting initial step to:", initialStep);
    setCurrentStep(initialStep);

    // Ensure URL is correct (but don't trigger navigation events during init)
    if (
      enableRouting &&
      window.location.hash !== `#${getStepSlug(initialStep)}`
    ) {
      const slug = getStepSlug(initialStep);
      const newHash = `#${slug}`;
      const currentUrl = window.location.href.split("#")[0];
      window.history.replaceState(null, "", `${currentUrl}${newHash}`);
    }

    // Mark as initialized
    setIsInitialized(true);

    console.log("=== INITIALIZATION COMPLETE ===");
  }, [
    steps.length,
    persistProgress,
    storageKey,
    showDraftResume,
    enableRouting,
    getCurrentStepFromURL,
  ]);

  // Save progress to localStorage - ONLY if showDraftResume is true (normal mode)
  const saveProgress = useCallback(() => {
    if (showDraftResume && persistProgress && storageKey) {
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            currentStep,
            completedSteps: Array.from(completedSteps),
            skippedSteps: Array.from(skippedSteps),
            stepData,
            timestamp: Date.now(),
            version: "1.0.0",
          } as StepperStorageFormat)
        );
        setHasStoredProgress(true);
      } catch (error) {
        console.error("Failed to save stepper progress:", error);
      }
    }
  }, [
    currentStep,
    completedSteps,
    skippedSteps,
    stepData,
    persistProgress,
    storageKey,
    showDraftResume,
  ]);

  // Auto-save functionality - Always enabled for memory (draft mode) or localStorage (normal mode)
  useEffect(() => {
    if (autoSave && isInitialized) {
      const timer = setTimeout(saveProgress, 500);
      return () => clearTimeout(timer);
    }
  }, [autoSave, saveProgress, stepData, isInitialized]);

  // Resume from draft (switch to normal mode)
  const resumeFromDraft = () => {
    console.log("Resuming from draft...");
    setIsDraftMode(false); // Exit draft mode (enable localStorage)
    setShowDraftBanner(false); // Hide banner
    // Keep current state and enable localStorage saving from now on
  };

  // Start fresh
  const startFresh = () => {
    console.log("Starting fresh...");
    const initialStep = enableRouting ? getCurrentStepFromURL() : 0;

    setCurrentStep(initialStep);
    setCompletedSteps(new Set());
    setSkippedSteps(new Set());
    setStepData({});
    setValidationErrors({});
    setDirtySteps(new Set());
    setOriginalStepData({});
    setDraftStep(0);

    if (enableRouting) {
      updateURL(initialStep);
    }

    setIsDraftMode(!showDraftResume); // Set draft mode based on prop
    setShowDraftBanner(false); // Hide banner after starting fresh
    setHasStoredProgress(false);

    // Clear localStorage if it exists
    if (persistProgress && storageKey) {
      localStorage.removeItem(storageKey);
      console.log("localStorage cleared");
    }
  };

  // Reset form (clear localStorage and restart)
  const resetForm = () => {
    console.log("Resetting form...");
    const initialStep = enableRouting ? getCurrentStepFromURL() : 0;

    setCurrentStep(initialStep);
    setCompletedSteps(new Set());
    setSkippedSteps(new Set());
    setStepData({});
    setValidationErrors({});
    setDirtySteps(new Set());
    setOriginalStepData({});
    setDraftStep(0);

    if (enableRouting) {
      updateURL(initialStep);
    }

    setHasStoredProgress(false);

    // Clear localStorage
    if (persistProgress && storageKey) {
      localStorage.removeItem(storageKey);
      console.log("localStorage cleared from reset");
    }
    window.location.reload();
  };

  // Navigate to step - URL controls navigation, so update URL and let hashchange handle the rest
  const goToStep = async (stepIndex, force = false) => {
    if (stepIndex < 0 || stepIndex >= steps.length) return;

    console.log("goToStep called:", stepIndex);

    // Save current step if needed
    if (
      showDraftResume &&
      saveOnStepChange &&
      enableApiSave &&
      onStepSave &&
      currentStep !== stepIndex
    ) {
      const isDirty = trackDirtyState ? dirtySteps.has(currentStep) : true;
      if (isDirty) {
        setSaveStatus("saving");
        try {
          await onStepSave(currentStep, stepData[currentStep], isDirty);
          setSaveStatus("saved");
          if (trackDirtyState) {
            setDirtySteps((prev) => {
              const newDirty = new Set(prev);
              newDirty.delete(currentStep);
              return newDirty;
            });
          }
        } catch (error) {
          setSaveStatus("error");
          console.error("Failed to save step data:", error);
          // Continue navigation even if save fails
        }
      }
    }

    // Update URL - this will trigger hashchange which will update currentStep
    if (enableRouting) {
      updateURL(stepIndex);
    } else {
      // If routing is disabled, update step directly
      const previousStep = currentStep;
      setCurrentStep(stepIndex);

      if (onStepChange) {
        onStepChange(stepIndex, previousStep, {
          stepData: stepData[stepIndex],
          isCompleted: completedSteps.has(stepIndex),
          isSkipped: skippedSteps.has(stepIndex),
        });
      }
    }
  };

  // Navigation functions for step components
  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      setCompletedSteps((prev) => new Set([...prev, currentStep]));
      goToStep(currentStep + 1, true);
    } else if (onComplete) {
      setCompletedSteps((prev) => new Set([...prev, currentStep]));
      onComplete(
        stepData,
        Array.from(completedSteps),
        Array.from(skippedSteps)
      );
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      goToStep(currentStep - 1, true);
    }
  };

  const handleSkip = () => {
    if (allowSkip && steps[currentStep]?.optional) {
      setSkippedSteps((prev) => new Set([...prev, currentStep]));
      if (currentStep < steps.length - 1) {
        goToStep(currentStep + 1, true);
      }
    }
  };

  const handleSave = async () => {
    // Always allow saving (memory for draft mode, localStorage for normal mode)
    if (enableApiSave && onStepSave) {
      const isDirty = trackDirtyState ? dirtySteps.has(currentStep) : true;
      if (isDirty) {
        setSaveStatus("saving");
        try {
          await onStepSave(currentStep, stepData[currentStep], isDirty);
          setSaveStatus("saved");
          if (trackDirtyState) {
            setDirtySteps((prev) => {
              const newDirty = new Set(prev);
              newDirty.delete(currentStep);
              return newDirty;
            });
          }
        } catch (error) {
          setSaveStatus("error");
          console.error("Failed to save step data:", error);
        }
      }
    } else {
      saveProgress();
      setSaveStatus("saved");
    }
  };

  // Validation functions for step components
  const setValidationError = (error) => {
    setValidationErrors((prev) => ({
      ...prev,
      [currentStep]: error,
    }));
  };

  const clearValidationError = () => {
    setValidationErrors((prev) => ({
      ...prev,
      [currentStep]: null,
    }));
  };

  // Update step data
  const updateStepData = (stepIndex, data) => {
    setStepData((prev) => {
      const newData = { ...prev[stepIndex], ...data };

      // Track dirty state if enabled
      if (trackDirtyState) {
        const original = originalStepData[stepIndex] || {};
        const isDirty = JSON.stringify(original) !== JSON.stringify(newData);

        setDirtySteps((prevDirty) => {
          const newDirty = new Set(prevDirty);
          if (isDirty) {
            newDirty.add(stepIndex);
          } else {
            newDirty.delete(stepIndex);
          }
          return newDirty;
        });
      }

      return {
        ...prev,
        [stepIndex]: newData,
      };
    });
  };

  // Get step status - URL navigation allows access to any step
  const getStepStatus = (stepIndex) => {
    if (completedSteps.has(stepIndex)) return "completed";
    if (skippedSteps.has(stepIndex)) return "skipped";
    if (stepIndex === currentStep) return "active";
    // All steps are accessible via URL
    return "available";
  };

  // Step circle component
  const StepCircle = ({ step, index, status }) => {
    // All steps are clickable since URL controls navigation
    const isClickable = true;

    const getCircleClasses = () => {
      const base =
        "w-8 h-8 rounded-full flex items-center justify-center font-medium text-xs transition-all duration-200";
      const clickable = "cursor-pointer hover:scale-105";

      switch (status) {
        case "completed":
          return `${base} ${clickable} bg-blue-600 text-white`;
        case "active":
          return `${base} ${clickable} bg-blue-600 text-white ring-2 ring-blue-200`;
        case "skipped":
          return `${base} ${clickable} bg-gray-300 text-gray-600`;
        default:
          return `${base} ${clickable} bg-gray-100 text-gray-700 border border-gray-300`;
      }
    };

    return (
      <div className="flex flex-col items-center min-w-0">
        <div className={getCircleClasses()} onClick={() => goToStep(index)}>
          {status === "completed" && !skippedSteps.has(index) ? (
            <Check size={12} />
          ) : status === "skipped" ? (
            <span className="text-xs">-</span>
          ) : (
            index + 1
          )}
        </div>
        <div className="mt-2 text-center max-w-20">
          <div
            className={`text-xs font-medium leading-tight ${
              status === "active" ? "text-blue-600" : "text-gray-700"
            }`}
          >
            {step.title}
          </div>
        </div>
      </div>
    );
  };

  // Connection line component
  const ConnectionLine = ({ isCompleted }) => (
    <div className="flex-1 mx-2 mt-4">
      <div
        className={`h-px transition-colors duration-300 ${
          isCompleted ? "bg-blue-600" : "bg-gray-300"
        }`}
      />
    </div>
  );

  const currentStepObj =
    steps && steps.length > 0 && currentStep >= 0 && currentStep < steps.length
      ? steps[currentStep]
      : null;
  const hasError = validationErrors[currentStep];

  // Show loading state if not initialized yet
  if (!isInitialized || steps.length === 0) {
    return (
      <div className={`w-full ${className}`}>
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          <div className="text-center text-gray-500">
            <div className="animate-pulse">Initializing stepper...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      {/* {showDraftResume && hasStoredProgress && !showDraftBanner && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <AlertCircle className="text-red-600" size={16} />
              <span className="text-sm font-medium text-red-800">
                Saved progress detected
              </span>
              <span className="text-sm text-red-600">
                Reset to start completely fresh
              </span>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={resetForm}
                className="px-3 py-1 text-xs font-medium text-red-800 bg-red-100 rounded hover:bg-red-200 transition-colors"
              >
                Reset Form
              </button>
            </div>
          </div>
        </div>
      )} */}

      {/* {showDraftBanner && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <RotateCcw className="text-amber-600" size={16} />
              <span className="text-sm font-medium text-amber-800">
                {showDraftResume
                  ? "Resume from where you left off?"
                  : "Working in draft mode"}
              </span>
              <span className="text-sm text-amber-600">
                {showDraftResume
                  ? `Step ${draftStep + 1}: ${steps[draftStep]?.title}`
                  : "Changes saved in memory only"}
              </span>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={resumeFromDraft}
                className="px-3 py-1 text-xs font-medium text-amber-800 bg-amber-100 rounded hover:bg-amber-200 transition-colors"
              >
                {showDraftResume ? "Resume" : "Enable Saving"}
              </button>
              <button
                onClick={startFresh}
                className="px-3 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
              >
                Start Fresh
              </button>
            </div>
          </div>
        </div>
      )} */}
      {/* 
      {isDraftMode && !showDraftBanner && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mb-4">
          <div className="flex items-center space-x-2">
            <Info className="text-blue-600" size={14} />
            <span className="text-xs text-blue-800">
              Draft mode: Changes are only saved in memory
            </span>
          </div>
        </div>
      )} */}

      <div className="bg-gray-50 rounded-lg p-4 mb-4">
        <div className="flex items-start">
          {steps && steps.length > 0 ? (
            steps.map((step, index) => (
              <React.Fragment key={index}>
                <StepCircle
                  step={step}
                  index={index}
                  status={getStepStatus(index)}
                />
                {index < steps.length - 1 && (
                  <ConnectionLine isCompleted={completedSteps.has(index)} />
                )}
              </React.Fragment>
            ))
          ) : (
            <div className="text-gray-500 text-sm">Loading steps...</div>
          )}
        </div>
      </div>

      {/* Step Content */}
      {currentStepObj ? (
        <div className="bg-white rounded-lg border border-gray-200">
          {/* Info Banner */}
          {currentStepObj.infoDescription && (
            <div className="flex items-start space-x-3 p-3 bg-blue-50 border-b border-blue-100">
              <Info className="text-blue-600 mt-0.5 flex-shrink-0" size={16} />
              <div className="text-sm text-blue-800">
                {currentStepObj.infoDescription}
              </div>
            </div>
          )}

          <div className="p-4">
            {/* Step Title */}
            <h2 className="text-lg font-medium text-gray-900 mb-1">
              {currentStepObj.title}
            </h2>
            <h5 className="text-sm font-medium text-gray-500 mb-4">
              {currentStepObj.description}
            </h5>

            {/* Error Message */}
            {hasError && (
              <div className="flex items-center space-x-2 mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
                <AlertCircle className="text-red-600 flex-shrink-0" size={16} />
                <span className="text-sm text-red-800">{hasError}</span>
              </div>
            )}

            {/* Step Content */}
            <div className="mb-6">
              {/* Render step component with enhanced props */}
              {currentStepObj.component ? (
                React.cloneElement(currentStepObj.component, {
                  // Existing props
                  data: stepData[currentStep] || {},
                  onDataChange: (data) => updateStepData(currentStep, data),
                  isActive: true,

                  // Navigation functions
                  onNext: handleNext,
                  onPrevious: handlePrevious,
                  onSkip: handleSkip,
                  onGoToStep: goToStep,

                  // Step context
                  currentStepIndex: currentStep,
                  totalSteps: steps.length,
                  isFirstStep: currentStep === 0,
                  isLastStep: currentStep === steps.length - 1,

                  // Step state
                  isCompleted: completedSteps.has(currentStep),
                  isSkipped: skippedSteps.has(currentStep),
                  completedSteps: Array.from(completedSteps),
                  skippedSteps: Array.from(skippedSteps),

                  // Validation
                  setValidationError,
                  clearValidationError,
                  isValidating,

                  // Data management
                  allStepData: stepData,

                  // Configuration
                  canSkip: allowSkip && currentStepObj.optional,
                  allowNavigation: {
                    canGoBack: currentStep > 0,
                    canGoForward: currentStep < steps.length - 1,
                  },

                  // Optional features
                  onSave: handleSave,
                  isDirty: trackDirtyState
                    ? dirtySteps.has(currentStep)
                    : false,
                  saveStatus,
                  isDraftMode, // Pass draft mode status to step components
                  stepHelpers: createStepHelpers(), // Pass step helpers to step components
                })
              ) : (
                <div className="p-6 text-center text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
                  Step content will be rendered here
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        // Loading state when steps are not ready
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          <div className="text-center text-gray-500">
            <div className="animate-pulse">Loading step content...</div>
          </div>
        </div>
      )}
    </div>
  );
};
